import dayjs from 'dayjs'
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Helmet } from 'react-helmet'
import { Link, useLocation, useParams } from 'react-router-dom'

import { AddIcon } from '@/components/icons'
import { DealerHeader } from '@/components/layout/DealerLayout'
import { useDealer } from '@/hooks/Dealer'
import RoIntakeNotEnabled from '@/pages/RoIntake/components/RoIntakeNotEnabled'
import SetStatusModal from '@/pages/RoIntake/SetStatusModal'
import { DISPOSITION, rowPermissions } from '@/pages/WorkQueue/queue'
import QueueKpiRow, { WorkSummary } from '@/pages/WorkQueue/QueueKpiRow'
import useRowEdits from '@/pages/WorkQueue/useRowEdits'
import { createRepairOrderExport, getRepairOrderExport } from '@/services/dealer.service'
import {
  getRoStatusCount,
  getRoStatusList,
  getRoStatusSummary,
  getWorkQueueCounts,
} from '@/services/repairOrders.service'
import { TaskStoreContext } from '@/stores/TaskStore'
import { UserStoreContext } from '@/stores/UserStore'
import { toast } from '@/utils/helpers'

import { AGING_BANDS, BUCKET, BUCKET_FILTERS, IN_PROGRESS_BUCKETS } from './buckets'
import ExportButton from './ExportButton'
import RoListPanel from './RoListPanel'
import StatusMatrix from './StatusMatrix'
import VolumeTrend from './VolumeTrend'
import { queueLink } from './workload'

// Received volume is hidden for now, not removed. Not rendered at all while off, so it
// does not fetch its series either. Flip to bring it back.
const SHOW_VOLUME_TREND = false

const DEFAULT_CELL = { periodId: 'today', bucket: BUCKET.READY }

// Rows per page in the RO list panel.
const PAGE_SIZE = 50

/**
 *
 * RoStatusDashboard
 *
 * WARP-943 — the RO status dashboard from Mark's September prototype, on live data.
 *
 * One dealer's dashboard, at /ro-intake/:dealerId; bare /ro-intake is every dealer's
 * workload (AllDealersWorkload, WARP-960). On 2026-09-17 WPC agreed the per-dealer
 * RO Intake page is largely replaced by this one — Emily: *"It feels very redundant."*
 * Michelle added that two similar pages confuse new users. A Log ROs button stays, in
 * the header.
 *
 * The **row names** are still the open question: WARP's seven statuses do not line up
 * with the prototype's buckets, so this page shows WARP's own and names the gap on
 * screen rather than inventing a mapping. See the note in `buckets.js`.
 */
const RoStatusDashboard = () => {
  const { isWpcUser, isDealerUser, isDealerAdmin, isImpersonating, isReadOnlySession } =
    useContext(UserStoreContext)
  const { createNewTask } = useContext(TaskStoreContext)
  const isROServiceUser = isDealerUser && !isDealerAdmin
  const { dealerId } = useParams()
  const location = useLocation()

  const [selected, setSelected] = useState(DEFAULT_CELL)

  const [summary, setSummary] = useState(null)
  const [loadingSummary, setLoadingSummary] = useState(false)
  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [loadingRows, setLoadingRows] = useState(false)
  // The panel's page and sort. Oldest first by default: the bottom of a pile of open
  // work is what people want to see.
  const [page, setPage] = useState(1)
  const [orderBy, setOrderBy] = useState('received_at')
  const [agingCounts, setAgingCounts] = useState(null)

  // A new cell starts back on page one, oldest first, and empty: the last cell's rows
  // stay on screen (dimmed) through a page or sort change, but not into another cell.
  const selectCell = (cell) => {
    setRows([])
    setSelected(cell)
    setPage(1)
    setOrderBy('received_at')
  }

  const summaryAbort = useRef(null)
  // Bumped after a status change from the RO list, so the table and the list reload.
  const [refreshKey, setRefreshKey] = useState(0)
  // Manage Repair Order for one row: opened from an RO number, or by picking Outstanding
  // List, which needs that form's required fields.
  const [modalRo, setModalRo] = useState(null)
  const listAbort = useRef(null)

  const handleErrors = (message) => toast(message, 'error')

  // The RO Intake spreadsheet. It moved here from the retired RO Claims view
  // (2026-09-28), which was the only place it lived. Built in the background; the task
  // tray downloads it when it is ready.
  const [exporting, setExporting] = useState(false)
  const handleExport = async () => {
    const exportTask = await createRepairOrderExport(
      `/dealers/${dealerId}/repair-order-exports/`,
      handleErrors,
      setExporting,
      { exportType: 'intake' },
    )
    if (!exportTask) return
    createNewTask('Download', exportTask, {
      label: 'RO Intake',
      filename: `${dealer?.name || 'Dealer'} - RO Intake.xlsx`,
      checkUpdatedTask: () => getRepairOrderExport(dealerId, exportTask.id, handleErrors),
    })
  }

  // The header's dealer, from the cache it shares with the Outstanding List, Billing,
  // and Log ROs, so switching between them does not flash a skeleton.
  const { dealer, error: dealerError } = useDealer(dealerId)

  useEffect(() => {
    if (dealerError) handleErrors(dealerError)
  }, [dealerError])

  // The dealer's Work Queue counts, for the same tiles the all-dealers dashboard shows.
  // Cleared on a dealer change, so the last dealer's numbers never show under this one.
  const [queueCounts, setQueueCounts] = useState(null)
  useEffect(() => {
    if (!dealerId) return undefined
    const controller = new AbortController()
    getWorkQueueCounts(dealerId, controller.signal).then((response) => {
      if (!controller.signal.aborted) setQueueCounts(response)
    })
    return () => controller.abort()
  }, [dealerId, refreshKey])
  useEffect(() => setQueueCounts(null), [dealerId])

  // The matrix: one request for every cell.
  useEffect(() => {
    if (!dealerId) return undefined

    summaryAbort.current?.abort()
    const controller = new AbortController()
    summaryAbort.current = controller

    setLoadingSummary(true)
    getRoStatusSummary(dealerId, handleErrors, controller.signal).then((response) => {
      if (controller.signal.aborted) return
      setLoadingSummary(false)
      if (response) setSummary(response)
    })

    return () => controller.abort()
  }, [dealerId, refreshKey])

  // The selected cell's received-date range, as dates. Read off the API's ISO strings,
  // which are already in the dealer's time zone; parsing them as instants would move
  // them into the browser's zone and could shift a boundary by a day.
  const selectedRange = useMemo(() => {
    // Everything received before the prior year, for the older-ROs flag.
    if (selected?.periodId === 'older' && summary?.beforePrior) {
      return { from: '2000-01-01', to: summary.beforePrior.to.slice(0, 10) }
    }
    const period = summary?.periods.find((row) => row.id === selected?.periodId)
    if (!period) return null
    return { from: period.from.slice(0, 10), to: period.to.slice(0, 10) }
  }, [summary, selected?.periodId])

  // One page of the list behind the selected cell.
  useEffect(() => {
    if (!dealerId || !selected || !selectedRange) return undefined

    listAbort.current?.abort()
    const controller = new AbortController()
    listAbort.current = controller

    setLoadingRows(true)
    getRoStatusList(
      dealerId,
      selectedRange,
      BUCKET_FILTERS[selected.bucket],
      { page, pageSize: PAGE_SIZE, orderBy },
      handleErrors,
      controller.signal,
    ).then((response) => {
      if (controller.signal.aborted) return
      setLoadingRows(false)
      if (!response) return
      setRows(response.results || [])
      setTotal(response.count ?? (response.results || []).length)
    })

    return () => controller.abort()
  }, [dealerId, selected, selectedRange, page, orderBy, refreshKey])

  // The aging strip counts the whole cell, not the page on screen, so each band is its
  // own count: the cell's statuses, received inside both the period and the band.
  useEffect(() => {
    setAgingCounts(null)
    if (!dealerId || !selected || !selectedRange || !summary) return undefined
    if (!IN_PROGRESS_BUCKETS.includes(selected.bucket)) return undefined

    const controller = new AbortController()
    const today = dayjs(summary.asOf.slice(0, 10))
    const bands = AGING_BANDS.map((band, index) => {
      const minDays = index === 0 ? 0 : AGING_BANDS[index - 1].max + 1
      const from = Number.isFinite(band.max)
        ? today.subtract(band.max, 'day').format('YYYY-MM-DD')
        : selectedRange.from
      const to = today.subtract(minDays, 'day').format('YYYY-MM-DD')
      // Clamp to the period; a band wholly outside it counts nothing.
      const range = {
        from: from > selectedRange.from ? from : selectedRange.from,
        to: to < selectedRange.to ? to : selectedRange.to,
      }
      return { band, range }
    })

    Promise.all(
      bands.map(({ range }) =>
        range.from > range.to
          ? 0
          : getRoStatusCount(dealerId, range, BUCKET_FILTERS[selected.bucket], controller.signal),
      ),
    ).then((counts) => {
      if (controller.signal.aborted) return
      setAgingCounts(bands.map(({ band }, index) => ({ band, count: counts[index] ?? 0 })))
    })

    return () => controller.abort()
  }, [dealerId, selected, selectedRange, summary])

  const periods = useMemo(() => summary?.periods || [], [summary])

  // The SLA signals (late counts, time in queue) are WPC's alone. Dealer users must
  // never receive them, per §3 of the handoff spec, and the API does not send them.
  // Mark's prototype had a WPC view / Dealer view toggle, but that was only how it
  // demonstrated both audiences; each user simply sees their own.
  const showWpcSignals = isWpcUser

  // The Work Queue tab that holds each of these rows (WARP-959), for every user.
  // The row labels link to the first three; the two reviews' labels keep their
  // Outstanding List tabs, and the panel links all five.
  const workQueueLabelLinks = dealerId
    ? {
        [BUCKET.READY]: `/work-queue/${dealerId}`,
        [BUCKET.CLOSED]: `/work-queue/${dealerId}?tab=closed`,
        [BUCKET.PENDING]: `/work-queue/${dealerId}?tab=pending`,
      }
    : {}
  const workQueueLinks = dealerId
    ? {
        ...workQueueLabelLinks,
        [BUCKET.OUTSTANDING_ADMIN]: `/work-queue/${dealerId}?tab=admin-review`,
        [BUCKET.OUTSTANDING_DEALER]: `/work-queue/${dealerId}?tab=dealer-review`,
      }
    : {}

  const dealerName = dealer?.name
  // Hidden until the dealer loads, then only for a dealer that can log ROs — the same
  // rule the nav bar's Log ROs item follows.
  const canLogRos = Boolean(dealer?.isRoIntakeEnabled)

  // Who may do what from a row of the RO list, by the Work Queue's rule, so a user can do
  // the same in both: WPC and a self-managed dealer's own admins work the list; everyone
  // adds files (WARP-966).
  const canWork = isWpcUser || (isDealerAdmin && Boolean(dealer?.isDealerManaged))
  const permissions = rowPermissions({ canWork, isReadOnlySession })

  // Edits staged on the RO list's rows (2026-09-24: "having the other options right here
  // on the screen"), saved by a row's Save or by Save all. A different cell is a
  // different list, so nothing staged carries over to it. One refresh per save.
  const rowEdits = useRowEdits({
    resetKey: `${dealerId}|${selected?.bucket}|${selected?.periodId}`,
    onSaved: ({ saved }) => {
      if (saved.length > 0) setRefreshKey((key) => key + 1)
    },
  })

  return (
    <>
      <Helmet>
        <title>{dealerName ? `Dashboard · ${dealerName}` : 'Dashboard'}</title>
      </Helmet>

      {/* Same header as the Outstanding List and Billing: the dealer's name and contact
          details. The name lives here only, so the page itself carries no title. */}
      <DealerHeader dealer={dealer} isROServiceUser={isROServiceUser} />

      {dealer?.isRoIntakeEnabled === false ? (
        <RoIntakeNotEnabled
          dealerId={dealerId}
          what="The dashboard counts the repair orders a dealer logs through RO Intake."
        />
      ) : (
        <div className="flex w-full flex-col space-y-4">
          {/* The same header as the all-dealers dashboard, plus Log ROs (2026-10-12). Log ROs
              is the one write action on the page. 2026-09-17: the per-dealer RO Intake page
              is largely replaced by this one, and "a Log ROs button stays, in the header". */}
          <div className="flex flex-wrap items-center justify-between gap-4">
            <WorkSummary counts={queueCounts} loading={!queueCounts} showLate={showWpcSignals} />
            {canLogRos && (
              <div className="flex gap-2">
                <ExportButton
                  // Exporting writes a job as the signed-in user, so not while viewing as
                  // someone else (the same rule the RO Claims button had).
                  disabled={isImpersonating}
                  exporting={exporting}
                  label="Export RO Intake"
                  onClick={handleExport}
                />
                <Link
                  className="flex h-[38px] items-center gap-2 rounded border-[1.5px] border-blue-800 bg-blue-800 px-4 text-xs font-bold uppercase tracking-widest text-white transition-colors hover:border-blue-900 hover:bg-blue-900"
                  state={{ from: location.pathname }}
                  to={`/ro-intake/${dealerId}/log`}
                >
                  <AddIcon className="h-4 stroke-[#ffffff]" />
                  Log ROs
                </Link>
              </div>
            )}
          </div>

          {/* The all-dealers dashboard's tiles, for this dealer alone: each opens its tab in
              this dealer's Work Queue. */}
          <QueueKpiRow
            counts={queueCounts}
            linkFor={(queue, filter) => queueLink(queue, { dealerId, filter })}
            loading={!queueCounts}
            showWpcSignals={showWpcSignals}
          />

          <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-2" id="ro-status-panel">
            <StatusMatrix
              beforePrior={summary?.beforePrior}
              // Status words link to their lists (2026-09-24). The ready queue is WPC's.
              labelLinks={
                dealerId
                  ? {
                      ...workQueueLabelLinks,
                      [BUCKET.OUTSTANDING]: `/dealer/${dealerId}/detail`,
                      // Each review side opens its own Outstanding List tab.
                      [BUCKET.OUTSTANDING_ADMIN]: `/dealer/${dealerId}/detail?tab=wpc-review`,
                      [BUCKET.OUTSTANDING_DEALER]: `/dealer/${dealerId}/detail?tab=dealer-review`,
                      [BUCKET.RETURNED]: `/dealer/${dealerId}/detail`,
                    }
                  : {}
              }
              loading={loadingSummary}
              oemName={dealer?.manufacturer?.name}
              onSelect={selectCell}
              periods={periods}
              selected={selected}
              selfManaged={Boolean(dealer?.isDealerManaged)}
              showLate={showWpcSignals}
            />

            {/* Stretched to the table's height, for the panel to fill and scroll in. */}
            <div className="relative xl:self-stretch">
              <RoListPanel
                dealerId={dealerId}
                loading={loadingRows}
                agingCounts={agingCounts}
                lateTotal={
                  // Late is Ready past the SLA, so only Ready and Received can hold any. The
                  // summary has the count for the whole cell, not just the page.
                  showWpcSignals && [BUCKET.READY, BUCKET.RECEIVED].includes(selected?.bucket)
                    ? periods.find((row) => row.id === selected.periodId)?.late?.ready ?? 0
                    : 0
                }
                onClose={() => setSelected(null)}
                onPageChange={setPage}
                onSortChange={(next) => {
                  setOrderBy(next)
                  setPage(1)
                }}
                orderBy={orderBy}
                page={page}
                pageSize={PAGE_SIZE}
                rows={rows}
                selected={selected}
                selfManaged={Boolean(dealer?.isDealerManaged)}
                onOpenOutstanding={(ro) =>
                  setModalRo({ ro, initialStatus: DISPOSITION.OUTSTANDING, fromRow: true })
                }
                onOpenRo={(ro) => setModalRo({ ro, initialStatus: null })}
                permissions={permissions}
                rowEdits={rowEdits}
                workQueueLinks={workQueueLinks}
                showWpcColumns={showWpcSignals}
                total={total}
              />
            </div>
          </div>

          {modalRo && (
            <SetStatusModal
              dealer={dealer}
              dealerId={dealerId}
              // From the row's Outstanding List chip: the flag and files staged on the row
              // carry into the form, so nothing picked there is lost.
              initialDealerClosed={
                modalRo.fromRow ? rowEdits.editFor(modalRo.ro).dealerClosed : undefined
              }
              initialNewFiles={modalRo.fromRow ? rowEdits.editFor(modalRo.ro).newFiles : undefined}
              initialStatus={modalRo.initialStatus}
              isDealerAdmin={isDealerAdmin}
              isDealerUser={isDealerUser}
              onSuccess={() => {
                // The form saved the RO itself, so anything staged on its row is stale.
                rowEdits.dropEdits([modalRo.ro.id])
                setRefreshKey((key) => key + 1)
              }}
              open={Boolean(modalRo)}
              repairOrder={modalRo.ro}
              setOpen={(open) => {
                if (open) return
                // Closed without saving: the Outstanding List fields are required, so the
                // row's choice cannot stand without them. Its flag and files stay staged.
                if (modalRo.fromRow) rowEdits.updateEdit(modalRo.ro, { disposition: null })
                setModalRo(null)
              }}
            />
          )}

          {SHOW_VOLUME_TREND && <VolumeTrend dealerId={dealerId} />}
        </div>
      )}
    </>
  )
}

export default RoStatusDashboard
